import React, { useState, useRef, useEffect } from 'react';

const SimpleAudioPlayer = ({ audioUrl, fileName, fileSize, isSender = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleError = (e) => {
      console.error('Audio playback error:', e);
      console.log('Audio source:', audioUrl);
      console.log('Audio element error details:', audio.error);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl]);

  const togglePlay = async () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      try {
        await audioRef.current.play();
      } catch (error) {
        console.error('Error playing audio:', error);
        alert('Cannot play audio. Please check if the audio format is supported.');
      }
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`p-4 rounded-lg ${isSender ? 'bg-green-50 dark:bg-green-900' : 'bg-gray-50 dark:bg-gray-700'} border ${isSender ? 'border-green-200 dark:border-green-700' : 'border-gray-200 dark:border-gray-600'}`}>
      {/* Audio Player Header */}
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
            {fileName || 'Voice Message'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {fileSize ? formatFileSize(fileSize) : 'Audio file'}
          </p>
        </div>
      </div>

      {/* Simple Audio Controls */}
      <div className="flex items-center justify-between">
        <button
          onClick={togglePlay}
          className="w-10 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full flex items-center justify-center transition-colors duration-200"
        >
          {isPlaying ? (
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" />
              <rect x="14" y="4" width="4" height="16" />
            </svg>
          ) : (
            <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          )}
        </button>

        <div className="flex-1 mx-4">
          <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>

        <div className="text-xs text-gray-500 dark:text-gray-400">
          {audioUrl ? 'Ready' : 'No Audio'}
        </div>
      </div>

      {/* Debug info */}
      <div className="mt-2 p-2 bg-yellow-100 dark:bg-yellow-900 rounded text-xs">
        <p>Audio URL: {audioUrl ? '✅ Valid' : '❌ Invalid'}</p>
        <p>Audio Type: {audioUrl?.includes('blob:') ? 'Blob URL' : 'Other'}</p>
        <p>Status: {isPlaying ? '▶️ Playing' : '⏸️ Paused'}</p>
      </div>

      {/* Audio Element - NOT hidden */}
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
        controls={false}
        className="w-full mt-2"
      />
    </div>
  );
};

export default SimpleAudioPlayer;
